// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import {
  ACTIVITY_FILTER_DEFAULTS,
  type ActivityEntry,
  type ActivityFilterState,
  ActivityTimeline,
} from "@/components/shared/activity-timeline";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";

function generateMockData(): ActivityEntry[] {
  const now = Date.now();
  const day = 86_400_000;
  return [
    {
      id: 1,
      timestamp: new Date(now - 0.6 * day).toISOString(),
      user: "admin@focal.systems",
      action: "deactivated",
      target: "Vendor",
      message: "Disabled while the integration was migrated.",
    },
    {
      id: 2,
      timestamp: new Date(now - 3 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Vendor",
      field: "chainName",
      oldValue: "acme",
      newValue: "acme_north",
    },
    {
      id: 3,
      timestamp: new Date(now - 9 * day).toISOString(),
      user: "admin@focal.systems",
      action: "activated",
      target: "Vendor",
    },
    {
      id: 4,
      timestamp: new Date(now - 18 * day).toISOString(),
      user: "admin@focal.systems",
      action: "created",
      target: "Vendor",
    },
  ];
}

const MOCK_DATA = generateMockData();

interface VendorActivityTabProps {
  vendorName: string;
  initialFilters?: string;
}

export function VendorActivityTab({ vendorName, initialFilters }: VendorActivityTabProps) {
  const navigate = useNavigate();
  const initial = useMemo(() => decodeFilters(initialFilters, ACTIVITY_FILTER_DEFAULTS), [initialFilters]);

  const handleFilterChange = useCallback(
    (values: ActivityFilterState) => {
      const encoded = encodeFilters(values, ACTIVITY_FILTER_DEFAULTS);
      navigate({
        to: "/iam/vendors/$vendorName",
        params: { vendorName },
        search: { t: "activity", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [navigate, vendorName],
  );

  return <ActivityTimeline entries={MOCK_DATA} initialFilters={initial} onFilterChange={handleFilterChange} />;
}
